/**
 * DOS518 - Digital Operations System for Wildfire Management
 * Copyright (c) 2024-2026 Angelo Irtilaç — All rights reserved.
 *
 * HandoverModal — Checklist visuale passaggio consegne DOS
 */
import React, { useState } from 'react';
import { useAppStore } from '../../store/useAppStore';

interface HandoverModalProps {
  onClose: () => void;
}

type Semaforo = 'green' | 'yellow' | 'red';

interface CheckItem {
  id: string;
  label: string;
  detail: string;
  stato: Semaforo;
  checked: boolean;
}

const dot = (s: Semaforo) =>
  s === 'green' ? '#22c55e' : s === 'yellow' ? '#eab308' : '#ef4444';

const label = (s: Semaforo) =>
  s === 'green' ? 'OK' : s === 'yellow' ? 'VERIFICA' : 'CRITICO';

export const HandoverModal: React.FC<HandoverModalProps> = ({ onClose }) => {
  const coau          = useAppStore(s => s.coau);
  const personale     = useAppStore(s => s.personale);
  const mezziAerei    = useAppStore(s => s.mezziAerei);
  const mezziTerrestri = useAppStore(s => s.mezziTerrestri);
  const laces         = useAppStore(s => (s as any).laces);
  const diario        = useAppStore(s => (s as any).diario ?? []);

  const buildItems = (): CheckItem[] => [
    {
      id: 'coau',
      label: 'Scheda COAU compilata',
      detail: coau?.comune
        ? `📍 ${[coau.comune, coau.provincia].filter(Boolean).join(', ')} — Ora: ${coau.oraAccertamento ?? 'N.D.'}`
        : 'Nessun intervento avviato',
      stato: coau?.comune ? 'green' : 'red',
      checked: false,
    },
    {
      id: 'dos',
      label: 'Nominativo DOS attivo',
      detail: coau?.nominativoDOS ? `DOS: ${coau.nominativoDOS}` : 'Sigla DOS non impostata',
      stato: coau?.nominativoDOS ? 'green' : 'yellow',
      checked: false,
    },
    {
      id: 'coord',
      label: 'Coordinate incendio',
      detail: coau?.latitudine && coau?.longitudine
        ? `LAT ${coau.latitudine}  LON ${coau.longitudine}`
        : 'Coordinate non acquisite',
      stato: coau?.latitudine ? 'green' : 'red',
      checked: false,
    },
    {
      id: 'personale',
      label: 'Personale assegnato',
      detail: `${personale.length} operator${personale.length === 1 ? 'e' : 'i'} in forza`,
      stato: personale.length >= 3 ? 'green' : personale.length > 0 ? 'yellow' : 'red',
      checked: false,
    },
    {
      id: 'mezzi_t',
      label: 'Mezzi terrestri',
      detail: `${mezziTerrestri.length} mezzo/i assegnato/i`,
      stato: mezziTerrestri.length > 0 ? 'green' : 'yellow',
      checked: false,
    },
    {
      id: 'mezzi_a',
      label: 'Mezzi aerei',
      detail: `${mezziAerei.length} aeromobile/i assegnato/i`,
      stato: mezziAerei.length > 0 ? 'green' : 'yellow',
      checked: false,
    },
    {
      id: 'laces',
      label: 'Zone LACES definite',
      detail: laces?.lookouts?.length > 0 ? 'Punti di osservazione configurati' : 'Nessuna zona LACES definita',
      stato: laces?.lookouts?.length > 0 ? 'green' : 'yellow',
      checked: false,
    },
    {
      id: 'diario',
      label: 'Diario operativo aggiornato',
      detail: diario.length > 0 ? `${diario.length} voce/i registrata/e` : 'Nessuna voce nel diario',
      stato: diario.length > 0 ? 'green' : 'yellow',
      checked: false,
    },
    {
      id: 'radio',
      label: 'Frequenza radio comunicata',
      detail: coau?.frequenzaRadio ? `Freq: ${coau.frequenzaRadio}` : 'Frequenza non registrata in COAU',
      stato: coau?.frequenzaRadio ? 'green' : 'yellow',
      checked: false,
    },
    {
      id: 'briefing',
      label: 'Briefing al DOS subentrante',
      detail: 'Confermato verbalmente',
      stato: 'yellow',
      checked: false,
    },
  ];

  const [items, setItems] = useState<CheckItem[]>(buildItems);

  const toggle = (id: string) =>
    setItems(prev => prev.map(i => i.id === id ? { ...i, checked: !i.checked } : i));

  const totale    = items.length;
  const ok        = items.filter(i => i.checked).length;
  const progPct   = Math.round((ok / totale) * 100);
  const critici   = items.filter(i => i.stato === 'red' && !i.checked).length;
  const prontezza = critici === 0 && ok >= totale * 0.8 ? 'green' : critici > 0 ? 'red' : 'yellow';

  return (
    <div style={S.overlay}>
      <div style={S.modal}>

        {/* Header */}
        <div style={S.header}>
          <div>
            <div style={S.title}>🔁 PASSAGGIO CONSEGNE DOS</div>
            <div style={S.sub}>Verifica ogni voce prima di trasferire il comando</div>
          </div>
          <button onClick={onClose} style={S.closeBtn}>✕</button>
        </div>

        {/* Barra progresso */}
        <div style={S.progressWrap}>
          <div style={S.progressBar}>
            <div style={{ ...S.progressFill, width: `${progPct}%`, background: dot(prontezza) }} />
          </div>
          <div style={S.progressInfo}>
            <span style={{ color: dot(prontezza), fontWeight: 700 }}>{ok} / {totale} voci verificate</span>
            <span style={{ ...S.prontezzaBadge, background: dot(prontezza) + '22', color: dot(prontezza), border: `1px solid ${dot(prontezza)}` }}>
              {prontezza === 'green' ? '✅ PRONTO AL TRASFERIMENTO' : prontezza === 'yellow' ? '⚠️ VERIFICA NECESSARIA' : '🚫 CRITICITÀ APERTE'}
            </span>
          </div>
        </div>

        {/* Lista checklist */}
        <div style={S.lista}>
          {items.map(item => (
            <div
              key={item.id}
              style={{
                ...S.riga,
                background: item.checked ? 'rgba(34,197,94,0.06)' : '#111827',
                borderColor: item.checked ? 'rgba(34,197,94,0.3)' : dot(item.stato) + '44',
                opacity: item.checked ? 0.75 : 1,
              }}
              onClick={() => toggle(item.id)}
            >
              {/* Semaforo */}
              <div style={{ ...S.semaforo, background: dot(item.stato), boxShadow: `0 0 6px ${dot(item.stato)}` }} />

              {/* Testo */}
              <div style={S.rigaBody}>
                <div style={{ ...S.rigaLabel, textDecoration: item.checked ? 'line-through' : 'none', color: item.checked ? '#64748b' : '#e2e8f0' }}>
                  {item.label}
                </div>
                <div style={S.rigaDetail}>{item.detail}</div>
              </div>

              {/* Badge stato */}
              <div style={{ ...S.statoBadge, color: dot(item.stato), border: `1px solid ${dot(item.stato)}44` }}>
                {label(item.stato)}
              </div>

              {/* Checkbox */}
              <div style={{
                ...S.checkbox,
                background: item.checked ? '#22c55e' : 'transparent',
                borderColor: item.checked ? '#22c55e' : '#334155',
              }}>
                {item.checked && <span style={{ color: '#000', fontSize: 13, fontWeight: 900 }}>✓</span>}
              </div>
            </div>
          ))}
        </div>

        {/* Footer */}
        <div style={S.footer}>
          <button onClick={() => setItems(buildItems())} style={S.btnReset}>↺ Reset</button>
          <div style={{ display: 'flex', gap: 8 }}>
            <button onClick={onClose} style={S.btnAnnulla}>Chiudi</button>
            <button
              style={{ ...S.btnConferma, opacity: prontezza !== 'red' ? 1 : 0.5 }}
              onClick={onClose}
            >
              ✅ Conferma passaggio
            </button>
          </div>
        </div>

      </div>
    </div>
  );
};

const S: Record<string, React.CSSProperties> = {
  overlay: {
    position: 'fixed', inset: 0,
    background: 'rgba(0,0,0,0.88)',
    display: 'flex', alignItems: 'center', justifyContent: 'center',
    zIndex: 9999, padding: 16,
  },
  modal: {
    background: '#0d1520',
    border: '1px solid #1e3a5f',
    borderRadius: 14,
    width: '100%', maxWidth: 660,
    maxHeight: '92vh',
    display: 'flex', flexDirection: 'column',
    boxShadow: '0 24px 64px rgba(0,0,0,0.8)',
    overflow: 'hidden',
  },
  header: {
    display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start',
    padding: '18px 22px 14px',
    borderBottom: '1px solid #1e3a5f',
    background: 'linear-gradient(135deg,#0d1520,#0f2040)',
    flexShrink: 0,
  },
  title: { fontSize: 17, fontWeight: 700, color: '#38bdf8' },
  sub:   { fontSize: 12, color: '#475569', marginTop: 3 },
  closeBtn: { background: 'none', border: 'none', color: '#64748b', fontSize: 20, cursor: 'pointer' },
  progressWrap: {
    padding: '14px 20px 10px',
    borderBottom: '1px solid #1e3a5f',
    flexShrink: 0,
  },
  progressBar: { height: 6, background: '#1e293b', borderRadius: 3, overflow: 'hidden', marginBottom: 8 },
  progressFill: { height: '100%', borderRadius: 3, transition: 'width 0.4s ease' },
  progressInfo: { display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 8 },
  prontezzaBadge: {
    fontSize: 11, fontWeight: 700, padding: '3px 10px', borderRadius: 20, letterSpacing: 0.5,
  },
  lista: { overflowY: 'auto', flex: 1, padding: '10px 14px', display: 'flex', flexDirection: 'column', gap: 6 },
  riga: {
    display: 'flex', alignItems: 'center', gap: 12,
    padding: '14px 14px', borderRadius: 10,
    border: '1px solid',
    cursor: 'pointer', transition: 'all 0.15s',
    minHeight: 64,
  },
  semaforo: { width: 10, height: 10, borderRadius: '50%', flexShrink: 0 },
  rigaBody: { flex: 1, minWidth: 0 },
  rigaLabel: { fontSize: 14, fontWeight: 600, marginBottom: 3 },
  rigaDetail: { fontSize: 11, color: '#64748b' },
  statoBadge: {
    fontSize: 10, fontWeight: 700, padding: '2px 8px', borderRadius: 4,
    flexShrink: 0, letterSpacing: 0.5,
  },
  checkbox: {
    width: 28, height: 28, borderRadius: 6, border: '2px solid',
    display: 'flex', alignItems: 'center', justifyContent: 'center',
    flexShrink: 0,
  },
  footer: {
    display: 'flex', justifyContent: 'space-between', alignItems: 'center',
    padding: '12px 16px', borderTop: '1px solid #1e3a5f',
    flexShrink: 0,
  },
  btnReset: {
    padding: '8px 14px', borderRadius: 6, border: '1px solid #334155',
    background: 'transparent', color: '#64748b', fontSize: 13, cursor: 'pointer',
  },
  btnAnnulla: {
    padding: '10px 18px', borderRadius: 6, border: '1px solid #1e3a5f',
    background: 'transparent', color: '#64748b', fontSize: 13, cursor: 'pointer',
  },
  btnConferma: {
    padding: '10px 22px', borderRadius: 6, border: 'none',
    background: 'linear-gradient(135deg,#15803d,#16a34a)',
    color: '#fff', fontSize: 14, fontWeight: 700, cursor: 'pointer',
    boxShadow: '0 4px 12px rgba(22,163,74,0.35)',
  },
};
